import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Select,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useQueries, useQuery } from "react-query";
import { Page } from "components/Page";
import { WithAuthorization, useAuth } from "context";
import { H1, H2 } from "components/text/headings";
import PillToggle from "pages/Reports/Insights/components/PillToggle";
import { getUsage, getUsageByContext, getUsageLabels } from "api/ai/ai.service";
import useAirUserRoles from "utils/AuthUtils";
import { useGetAllOrganizations } from "hooks/query/esg-admin/esg-admin";
import {
  getAiModelsAllocation,
  splitPctByAgents,
  splitPctByPerson,
  splitPctByTier,
} from "pages/OrgMgmt/aiModelsAllocation";
import { bucketCostByDay } from "./dailyUsage";
import MonthlyUsageChart from "./MonthlyUsageChart";
import AllowanceProgressChart from "./AllowanceProgressChart";
import CompletedTasksTable from "./CompletedTasksTable";
import CreditsAllowanceBar from "./CreditsAllowanceBar";
import {
  getAnnualPeriod,
  getMonthPeriodByOffset,
  getRecentMonthOptions,
  getTrailing30DaysPeriod,
} from "./allowancePeriods";
import {
  getLast12MonthsRange,
  getLastNMonthsRanges,
  getMonthRangeByOffset,
  getTrailing30DaysRange,
  getYearMonthsRanges,
  getYearToDateRange,
} from "./usageDateRanges";

// Values match AllowanceProgressChart's splitMode prop directly.
const VIEW_TOTAL = "total";
const VIEW_BY_TASK = "task";
const VIEW_BY_MODEL = "tier";
const VIEW_BY_PERSON = "person";

const PERIOD_MONTH = "month";
const PERIOD_TRAILING_30 = "trailing30";

const TASKS_MODE_ANNUAL = "annual";
const TASKS_MODE_MONTHLY = "monthly";

// Demo wiring: the annual AI usage LIMIT is read from the "Company" org (set via
// Settings → Global admin → Global Org Management → AI Models) — a placeholder
// until org-scoping is wired to the logged-in user's own org. The usage/SPEND figures
// below are real, though: getUsage() is already scoped to the caller's own org via
// their JWT, so no org-selection is needed for the numbers themselves.
const DEMO_ORG_NAME = "Company";

function sumCost(usageRows) {
  return (usageRows || []).reduce((sum, row) => sum + parseFloat(row.cost || 0), 0);
}

function AIStatus({ componentRequiredPermissions }) {
  const { getAccessTokenSilently } = useAuth();
  const { isESGAdmin } = useAirUserRoles();

  // Values match AllowanceProgressChart's splitMode prop directly — passed straight through.
  const [splitMode, setSplitMode] = useState(VIEW_TOTAL);

  const [secondaryPeriod, setSecondaryPeriod] = useState(PERIOD_MONTH);
  const showTrailing30 = secondaryPeriod === PERIOD_TRAILING_30;

  // Defaults to the current month (monthsAgo=0); the dropdown lets the user pick any
  // of the last 12 months instead.
  const monthOptions = getRecentMonthOptions(12);
  const [selectedMonthsAgo, setSelectedMonthsAgo] = useState(0);

  // Completed AI Tasks has its own Annual/Monthly switch, independent of the Usage
  // vs Allowance section above it. The Month/Last-30-Days sub-choice defaults to
  // whatever's selected above (captured once, on mount) but can then be changed
  // here without affecting the Usage vs Allowance section.
  const [tasksMode, setTasksMode] = useState(TASKS_MODE_MONTHLY);
  const [tasksSecondaryPeriod, setTasksSecondaryPeriod] = useState(secondaryPeriod);
  const [tasksSelectedMonthsAgo, setTasksSelectedMonthsAgo] = useState(selectedMonthsAgo);
  const tasksShowTrailing30 = tasksSecondaryPeriod === PERIOD_TRAILING_30;

  const { data: orgList, isLoading: isLoadingOrgs } = useGetAllOrganizations();
  const demoOrg = useMemo(() => orgList?.find((org) => org.name === DEMO_ORG_NAME), [orgList]);

  // The annual limit + per-agent %/tier split are set in Organisation Management
  // (frontend-only store for now — see pages/OrgMgmt/aiModelsAllocation.js).
  const allocation = getAiModelsAllocation(demoOrg?.tenantId);
  const annualLimitGbp = allocation.annualLimitGbp;
  const monthlyAllowanceGbp = annualLimitGbp ? annualLimitGbp / 12 : null;

  const yearToDateRange = getYearToDateRange();
  const selectedMonthRange = getMonthRangeByOffset(selectedMonthsAgo);
  const trailing30Range = getTrailing30DaysRange();
  // Completed AI Tasks' own Annual/Monthly + Month/Last-30-Days selection (see above) —
  // decoupled from the Usage vs Allowance section's range once the user changes it here.
  const tasksMonthRange = getMonthRangeByOffset(tasksSelectedMonthsAgo);
  const completedTasksRange =
    tasksMode === TASKS_MODE_ANNUAL ? yearToDateRange : tasksShowTrailing30 ? trailing30Range : tasksMonthRange;
  const last12MonthsRange = getLast12MonthsRange();
  const last12MonthRanges = useMemo(() => getLastNMonthsRanges(12), []);
  const yearMonthRanges = useMemo(() => getYearMonthsRanges(), []);

  // Real usage cost, fetched from the same /v0/usage endpoint the existing AI Usage
  // page uses (already scoped to the caller's own org). One query per date range —
  // fan-out via useQueries, same pattern as hooks/query/reports.js.
  const rangeQueries = useQueries(
    [
      { key: "yearToDate", ...yearToDateRange },
      { key: "selectedMonth", ...selectedMonthRange },
      { key: "trailing30", ...trailing30Range },
      ...last12MonthRanges.map((r, i) => ({ key: `last12_${i}`, dateFrom: r.dateFrom, dateTo: r.dateTo })),
      ...yearMonthRanges.map((r, i) => ({ key: `yearMonth_${i}`, dateFrom: r.dateFrom, dateTo: r.dateTo })),
    ].map((range) => ({
      queryKey: ["aiStatusUsage", range.key, range.dateFrom, range.dateTo],
      queryFn: () =>
        getAccessTokenSilently().then((token) => getUsage({ dateFrom: range.dateFrom, dateTo: range.dateTo }, token)),
    }))
  );

  // Daily breakdown for the monthly chart's line view, one query per line-view range
  // (This Year / Last 12 Months — mirrors the bar view's own toggle). Each is
  // task/context-level rows carrying a real cost + first_call date, bucketed
  // client-side by day (see dailyUsage.js — no daily-bucketed usage endpoint exists).
  const dailyRangeQueries = useQueries(
    [
      { key: "daily_year", ...yearToDateRange },
      { key: "daily_last12", ...last12MonthsRange },
    ].map((range) => ({
      queryKey: ["aiStatusDailyUsage", range.key, range.dateFrom, range.dateTo],
      queryFn: () =>
        getAccessTokenSilently().then((token) =>
          getUsageByContext({ dateFrom: range.dateFrom, dateTo: range.dateTo }, token)
        ),
    }))
  );
  const dailyUsageDataYear = useMemo(
    () => bucketCostByDay(dailyRangeQueries[0]?.data, yearToDateRange.dateFrom, yearToDateRange.dateTo),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yearToDateRange is a fresh object each render but its ISO strings are stable per day
    [dailyRangeQueries, yearToDateRange.dateFrom, yearToDateRange.dateTo]
  );
  const dailyUsageDataLast12 = useMemo(
    () => bucketCostByDay(dailyRangeQueries[1]?.data, last12MonthsRange.dateFrom, last12MonthsRange.dateTo),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- last12MonthsRange is a fresh object each render but its ISO strings are stable per day
    [dailyRangeQueries, last12MonthsRange.dateFrom, last12MonthsRange.dateTo]
  );

  // Individual AI task/question/report/chat rows for the currently selected period —
  // the same getUsageByContext rows shown on /app/settings/ai-usage's "LLM Usage by
  // Context" table — for the new "Completed AI Tasks" section below. Kept as its own
  // query (not folded into isLoadingUsage) so it doesn't block the gauges' first paint.
  const { data: completedTasksContext, isLoading: isLoadingCompletedTasks } = useQuery({
    queryKey: ["aiStatusCompletedTasksContext", completedTasksRange.dateFrom, completedTasksRange.dateTo],
    queryFn: () => getAccessTokenSilently().then((token) => getUsageByContext(completedTasksRange, token)),
  });

  // Resolves human-readable titles for question/round/report-writer-task rows —
  // same batch-lookup pattern as AIUsage.jsx's UsageViewer.
  const completedTasksLabelIds = useMemo(() => {
    const questionIds = new Set();
    const roundIds = new Set();
    const reportTaskIds = new Set();
    (completedTasksContext || []).forEach((c) => {
      if (c.context_type === "question" && c.context_id) questionIds.add(c.context_id);
      if (c.round_id) roundIds.add(c.round_id);
      if (c.context_type === "task" && c.task_type === "report_writer" && c.context_id) {
        reportTaskIds.add(c.context_id);
      }
    });
    return {
      questionIds: Array.from(questionIds).sort(),
      roundIds: Array.from(roundIds).sort(),
      reportTaskIds: Array.from(reportTaskIds).sort(),
    };
  }, [completedTasksContext]);

  const { data: completedTasksLabels } = useQuery({
    queryKey: ["aiStatusCompletedTasksLabels", completedTasksLabelIds],
    queryFn: () => getAccessTokenSilently().then((token) => getUsageLabels(completedTasksLabelIds, token)),
    enabled:
      completedTasksLabelIds.questionIds.length > 0 ||
      completedTasksLabelIds.roundIds.length > 0 ||
      completedTasksLabelIds.reportTaskIds.length > 0,
  });

  const isLoadingUsage = rangeQueries.some((q) => q.isLoading) || dailyRangeQueries.some((q) => q.isLoading);
  const costByKey = useMemo(() => {
    const map = {};
    const keys = [
      "yearToDate",
      "selectedMonth",
      "trailing30",
      ...last12MonthRanges.map((_, i) => `last12_${i}`),
      ...yearMonthRanges.map((_, i) => `yearMonth_${i}`),
    ];
    keys.forEach((key, i) => {
      map[key] = sumCost(rangeQueries[i]?.data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rangeQueries.length is stable per render (fixed range list)
  }, [rangeQueries, last12MonthRanges, yearMonthRanges]);

  const toPct = (spend, allowanceGbp) => (allowanceGbp ? (spend / allowanceGbp) * 100 : null);

  const annualPct = toPct(costByKey.yearToDate, annualLimitGbp);
  const monthPct = toPct(costByKey.selectedMonth, monthlyAllowanceGbp);
  const trailing30Pct = toPct(costByKey.trailing30, monthlyAllowanceGbp); // 30-day ≈ one month's slice

  const annualPeriod = getAnnualPeriod();
  const monthPeriod = getMonthPeriodByOffset(selectedMonthsAgo);
  const trailing30Period = getTrailing30DaysPeriod();
  const tasksPeriodLabel =
    tasksMode === TASKS_MODE_ANNUAL
      ? "this year"
      : tasksShowTrailing30
        ? "the last 30 days"
        : monthOptions[tasksSelectedMonthsAgo]?.label;

  const monthlyUsageData = last12MonthRanges.map((r, i) => ({
    month: r.month,
    pct_of_allowance: toPct(costByKey[`last12_${i}`], monthlyAllowanceGbp),
    spend_gbp: costByKey[`last12_${i}`],
  }));
  const yearUsageData = yearMonthRanges.map((r, i) => ({
    month: r.month,
    pct_of_allowance: toPct(costByKey[`yearMonth_${i}`], monthlyAllowanceGbp),
    spend_gbp: costByKey[`yearMonth_${i}`],
  }));

  if (isLoadingOrgs || isLoadingUsage) {
    return (
      <Page title="AI Status" componentRequiredPermissions={componentRequiredPermissions}>
        <Flex direction="column" gap={2} mx="10%" mt={4}>
          <Spinner />
        </Flex>
      </Page>
    );
  }

  return (
    <Page title="AI Status" componentRequiredPermissions={componentRequiredPermissions}>
      <Flex direction="column" gap={2} mx="10%">
        <Flex direction="column" gap={2}>
          <H1>AI Status</H1>
          <Text fontSize="body.m" color="gray.500">
            See how your organisation is using AI across the platform, and compare usage over time and by task type.
          </Text>
        </Flex>
        {annualLimitGbp ? (
          <CreditsAllowanceBar
            totalCredits={Math.ceil(annualLimitGbp)}
            usedCredits={Math.ceil(costByKey.yearToDate || 0)}
          />
        ) : (
          <Text fontSize="body.s" color="status.orange" fontWeight="600">
            No annual AI usage limit is set for {demoOrg?.name || DEMO_ORG_NAME}. Set one in Organisation Management (AI
            Models) to see usage as a percentage of your allowance below.
          </Text>
        )}

        <Accordion defaultIndex={[0, 1]} allowMultiple mt={4}>
          <AccordionItem borderTop="none" borderBottom="1px solid" borderColor="gray.200">
            <AccordionButton px={0} py={3}>
              <H2 fontSize="body.2xl" flex="1" textAlign="left" mt={0} mb={0}>
                Usage vs Allowance
              </H2>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} mt={4}>
              <Flex direction="column" gap={8}>
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                  <PillToggle
                    options={[
                      { label: "Total", value: VIEW_TOTAL },
                      { label: "By Task", value: VIEW_BY_TASK },
                      { label: "By Model", value: VIEW_BY_MODEL },
                      { label: "By Person", value: VIEW_BY_PERSON },
                    ]}
                    selected={splitMode}
                    setSelected={setSplitMode}
                  />
                  <PillToggle
                    options={[
                      { label: "Month", value: PERIOD_MONTH },
                      { label: "Last 30 Days", value: PERIOD_TRAILING_30 },
                    ]}
                    selected={secondaryPeriod}
                    setSelected={setSecondaryPeriod}
                  />
                </Flex>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={8}>
                  <AllowanceProgressChart
                    title="Annual usage"
                    description="Total usage so far this year, as a percentage of your annual allowance."
                    pct={annualPct || 0}
                    spendGbp={costByKey.yearToDate}
                    showSpend={isESGAdmin}
                    daysElapsed={annualPeriod.daysElapsed}
                    daysToReset={annualPeriod.daysToReset}
                    splitMode={splitMode}
                    breakdown={splitPctByAgents(annualPct || 0, allocation.agents)}
                    tierBreakdown={splitPctByTier(annualPct || 0)}
                    personBreakdown={splitPctByPerson(annualPct || 0)}
                  />
                  {showTrailing30 ? (
                    <AllowanceProgressChart
                      title="Last 30 days"
                      description="Usage over the trailing 30 days, as a percentage of a 30-day slice of your annual allowance."
                      pct={trailing30Pct || 0}
                      spendGbp={costByKey.trailing30}
                      showSpend={isESGAdmin}
                      daysElapsed={trailing30Period.daysElapsed}
                      daysToReset={trailing30Period.daysToReset}
                      splitMode={splitMode}
                      breakdown={splitPctByAgents(trailing30Pct || 0, allocation.agents)}
                      tierBreakdown={splitPctByTier(trailing30Pct || 0)}
                      personBreakdown={splitPctByPerson(trailing30Pct || 0)}
                    />
                  ) : (
                    <AllowanceProgressChart
                      titleContent={
                        <Select
                          size="sm"
                          variant="unstyled"
                          fontSize="body.m"
                          fontWeight="600"
                          color="gray.700"
                          w="fit-content"
                          value={selectedMonthsAgo}
                          onChange={(e) => setSelectedMonthsAgo(Number(e.target.value))}
                        >
                          {monthOptions.map((option) => (
                            <option key={option.monthsAgo} value={option.monthsAgo}>
                              {option.label}
                              {option.monthsAgo === 0 ? " (current)" : ""}
                            </option>
                          ))}
                        </Select>
                      }
                      description="Usage for the selected month, as a percentage of your monthly allowance (annual limit ÷ 12)."
                      pct={monthPct || 0}
                      spendGbp={costByKey.selectedMonth}
                      showSpend={isESGAdmin}
                      daysElapsed={monthPeriod.daysElapsed}
                      daysToReset={monthPeriod.daysToReset}
                      splitMode={splitMode}
                      breakdown={splitPctByAgents(monthPct || 0, allocation.agents)}
                      tierBreakdown={splitPctByTier(monthPct || 0)}
                      personBreakdown={splitPctByPerson(monthPct || 0)}
                    />
                  )}
                </SimpleGrid>
                <MonthlyUsageChart
                  data={monthlyUsageData}
                  yearData={yearUsageData}
                  dailyDataYear={dailyUsageDataYear}
                  dailyDataLast12={dailyUsageDataLast12}
                  annualLimitGbp={annualLimitGbp}
                  showSpend={isESGAdmin}
                />
              </Flex>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem borderTop="none" borderBottom="1px solid" borderColor="gray.200">
            <AccordionButton px={0} py={3}>
              <H2 fontSize="body.2xl" flex="1" textAlign="left" mt={0} mb={0}>
                Completed AI Tasks
              </H2>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} mt={4}>
              <Flex direction="column" gap={4}>
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                  <PillToggle
                    options={[
                      { label: "Annual", value: TASKS_MODE_ANNUAL },
                      { label: "Monthly", value: TASKS_MODE_MONTHLY },
                    ]}
                    selected={tasksMode}
                    setSelected={setTasksMode}
                  />
                  {tasksMode === TASKS_MODE_MONTHLY && (
                    <Flex align="center" gap={3} flexWrap="wrap">
                      <PillToggle
                        options={[
                          { label: "Month", value: PERIOD_MONTH },
                          { label: "Last 30 Days", value: PERIOD_TRAILING_30 },
                        ]}
                        selected={tasksSecondaryPeriod}
                        setSelected={setTasksSecondaryPeriod}
                      />
                      {!tasksShowTrailing30 && (
                        <Select
                          size="sm"
                          variant="unstyled"
                          fontSize="body.m"
                          fontWeight="600"
                          color="gray.700"
                          w="fit-content"
                          value={tasksSelectedMonthsAgo}
                          onChange={(e) => setTasksSelectedMonthsAgo(Number(e.target.value))}
                        >
                          {monthOptions.map((option) => (
                            <option key={option.monthsAgo} value={option.monthsAgo}>
                              {option.label}
                              {option.monthsAgo === 0 ? " (current)" : ""}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Flex>
                  )}
                </Flex>
                <Text fontSize="body.s" color="gray.500">
                  AI activity completed during {tasksPeriodLabel}.
                </Text>
                <CompletedTasksTable
                  rows={completedTasksContext}
                  usageLabels={completedTasksLabels}
                  isLoading={isLoadingCompletedTasks}
                />
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Flex>
    </Page>
  );
}

export default WithAuthorization(AIStatus);
